// ============================================
// Admin Dashboard — Premium Professional Design
// Hyper-glassmorphic UI with double theme support
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
// import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import adminService from '../../services/adminService';
import socketService from '../../services/socket';
import { GoogleMap, Marker, DirectionsService, DirectionsRenderer } from '@react-google-maps/api';
import AnimatedMarker from '../../components/common/AnimatedMarker';
import DriverInfoWindow from '../../components/common/DriverInfoWindow';
import { getCarMarkerIcon } from '../../components/common/CarMarkerIcon';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';
import { getPremiumMapOptions } from '../../utils/mapStyles';
import { useTheme } from '../../context/ThemeContext';
import { groupRoutes } from '../../utils/routeUtils';



// ── Premium SVG Icons ──────────────────────────
const icons = {
    drivers: <svg fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z" /></svg>,
    routes: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>,
    children: <svg fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg>,
    vehicle: <svg fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" /></svg>,
    school: <svg fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z" /></svg>,
    arrowUp: <svg viewBox="0 0 12 12" fill="none" className="w-3 h-3"><path d="M6 9V3M3.5 5.5L6 3l2.5 2.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>,
    refresh: <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5"><path stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" /></svg>,
    pa: <svg fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" /></svg>,
};

const mapContainerStyle = { width: '100%', height: '100%' };
const defaultCenter = { lat: 51.5074, lng: -0.1278 };

const AdminDashboard = () => {
    // const navigate = useNavigate();

    // ── Real data state ─────────────────────────
    const [stats, setStats] = useState({});
    const [drivers, setDrivers] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    
    // ── Directions State ────────────────────────
    const [directionsResponses, setDirectionsResponses] = useState({});
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [focusType, setFocusType] = useState(null); // 'car' or 'route'

    const { isLoaded } = useGoogleMaps();
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    // -- Fullscreen Map Logic --
    const mapContainerRef = React.useRef(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showDriverPanel, setShowDriverPanel] = useState(true);

    const toggleFullscreen = useCallback(() => {
        if (!mapContainerRef.current) return;
        if (!document.fullscreenElement) {
            mapContainerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => { });
        } else {
            document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => { });
        }
    }, []);

    useEffect(() => {
        const handleChange = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handleChange);
        return () => document.removeEventListener('fullscreenchange', handleChange);
    }, []);

    const mapOptions = React.useMemo(() => getPremiumMapOptions(isDark), [isDark]);



    const mapRef = React.useRef(null);
    const boundsFit = React.useRef(false);

    const onLoad = useCallback(function callback(map) { mapRef.current = map; }, []);
    const onUnmount = useCallback(function callback(map) { mapRef.current = null; }, []);

    const fetchData = useCallback(async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true);
        try {
            const [statsRes, driversRes, routesRes, jobsRes] = await Promise.all([
                adminService.getStats(), 
                adminService.getDrivers(), 
                adminService.getRoutes(),
                adminService.getJobs()
            ]);
            if (statsRes.data.success) setStats(statsRes.data.data.stats || {});
            if (driversRes.data.success) setDrivers(driversRes.data.data.drivers || []);
            if (routesRes.data.success) {
                const fetchedRoutes = groupRoutes(routesRes.data.data.routes || []);
                setRoutes(prev => {
                    const prevIds = prev.map(r => r._id).sort().join(',');
                    const newIds = fetchedRoutes.map(r => r._id).sort().join(',');
                    if (prevIds !== newIds) {
                        setDirectionsResponses({});
                    }
                    return fetchedRoutes;
                });
            }
            if (jobsRes.data.success) setJobs(jobsRes.data.data.jobs || []);
        } catch (err) {
            console.error('Dashboard fetch error:', err);
        } finally {
            setLoading(false); setRefreshing(false);
        }
    }, []);

    // ── Socket connection status tracking ──
    const [socketStatus, setSocketStatus] = useState(() => socketService.isConnected() ? 'connected' : 'disconnected');
    const eventCountRef = React.useRef(0);
    const [eventCount, setEventCount] = useState(0);
    const [showInfoCard, setShowInfoCard] = useState(false);

    // ── Ensure socket is connected & joined to admin room ──
    useEffect(() => {
        const ensureConnection = () => {
            try {
                const userStr = localStorage.getItem('user');
                if (userStr && userStr !== 'undefined') {
                    const user = JSON.parse(userStr);
                    if (user && user.role && user._id) {
                        socketService.connect(user.role, user._id);
                    }
                }
            } catch (e) { /* ignore */ }
            socketService.forceRejoin();
        };

        ensureConnection();

        const onConnect = () => {
            setSocketStatus('connected');
            socketService.forceRejoin();
        };
        const onDisconnect = () => setSocketStatus('disconnected');
        const onReconnect = () => {
            setSocketStatus('connected');
            socketService.forceRejoin();
        };

        socketService.on('connect', onConnect);
        socketService.on('disconnect', onDisconnect);
        socketService.on('reconnect', onReconnect);
        
        return () => {
            socketService.off('connect', onConnect);
            socketService.off('disconnect', onDisconnect);
            socketService.off('reconnect', onReconnect);
        };
    }, []);

    useEffect(() => { 
        fetchData(); 
        
        const handleLocationUpdate = (data) => {
            eventCountRef.current += 1;
            if (eventCountRef.current % 5 === 1) {
                setEventCount(eventCountRef.current);
            }
            setDrivers(prev => prev.map(d => d._id === data.userId ? { ...d, currentLocation: { type: 'Point', coordinates: [Number(data.lng), Number(data.lat)] }, heading: Number(data.heading) || 0, speed: Number(data.speed) || 0, isActive: true, lastLocationUpdate: new Date() } : d));
        };

        const handleStatusChange = (data) => {
            setDrivers(prev => prev.map(d => 
                d._id === data.driverId ? { ...d, isActive: data.isActive } : d
            ));
        };

        socketService.on('driver:location:update', handleLocationUpdate);
        socketService.on('driver:status:changed', handleStatusChange);
        const handleUpdate = () => fetchData(true);
        socketService.on('job:new', handleUpdate);
        socketService.on('job:updated', handleUpdate);
        socketService.on('route:updated', handleUpdate);
        socketService.on('driver:doc:updated', handleUpdate);
        socketService.on('driver:doc:uploaded', handleUpdate);
        socketService.on('driver:registered', handleUpdate);
        socketService.on('pa:registered', handleUpdate);

        return () => {
            socketService.off('driver:location:update', handleLocationUpdate);
            socketService.off('driver:status:changed', handleStatusChange);
            socketService.off('job:new', handleUpdate);
            socketService.off('job:updated', handleUpdate);
            socketService.off('route:updated', handleUpdate);
            socketService.off('driver:doc:updated', handleUpdate);
            socketService.off('driver:doc:uploaded', handleUpdate);
            socketService.off('driver:registered', handleUpdate);
            socketService.off('pa:registered', handleUpdate);
        };
    }, [fetchData]);
    
    const activeDrivers = drivers.filter(d => !d.isBlocked && d.isVerified);
    
    // Only show drivers who have at least one assigned active route OR assigned/active job
    const driversOnMap = activeDrivers.filter(d => 
        routes.some(r => r.isActive && String(r.driverId?._id || r.driverId) === String(d._id)) ||
        jobs.some(j => (j.status === 'assigned' || j.status === 'accepted' || j.status === 'started' || j.status === 'in_progress') && String(j.driverId?._id || j.driverId) === String(d._id))
    );
    
    useEffect(() => {
        if (!mapRef.current || driversOnMap.length === 0 || boundsFit.current) return;
        const bounds = new window.google.maps.LatLngBounds();
        let hasValid = false;
        driversOnMap.forEach(d => {
            const coords = d.currentLocation?.coordinates || d.location?.coordinates;
            // Strict check to prevent "blue sea" default zoom issues
            if (Array.isArray(coords) && coords.length === 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number' && coords[0] !== 0 && coords[1] !== 0) {
                bounds.extend({ lat: coords[1], lng: coords[0] });
                hasValid = true;
            }
        });
        
        // Also extend bounds to include active routes so they are fully visible
        routes.filter(r => r.isActive && r.schoolId).forEach(r => {
            const coords = r.schoolId.location?.coordinates;
            if (Array.isArray(coords) && coords.length === 2) {
                bounds.extend({ lat: coords[1], lng: coords[0] });
            }
            if (r.startLocation?.coordinates?.length === 2) {
                bounds.extend({ lat: r.startLocation.coordinates[1], lng: r.startLocation.coordinates[0] });
            }
            if (r.endLocation?.coordinates?.length === 2) {
                bounds.extend({ lat: r.endLocation.coordinates[1], lng: r.endLocation.coordinates[0] });
            }
        });
        
        // Also extend bounds to include active jobs
        jobs.filter(j => (j.status === 'assigned' || j.status === 'accepted' || j.status === 'started' || j.status === 'in_progress') && j.driverId).forEach(j => {
            if (j.pickupCoordinates?.length === 2) {
                bounds.extend({ lat: j.pickupCoordinates[1], lng: j.pickupCoordinates[0] });
            }
            if (j.dropoffCoordinates?.length === 2) {
                bounds.extend({ lat: j.dropoffCoordinates[1], lng: j.dropoffCoordinates[0] });
            }
        });

        if (hasValid) {
            mapRef.current.fitBounds(bounds);
            const listener = window.google.maps.event.addListener(mapRef.current, "idle", function() { 
                if (mapRef.current.getZoom() > 14) mapRef.current.setZoom(14); 
                window.google.maps.event.removeListener(listener); 
            });
            boundsFit.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [driversOnMap]);

    // Pan to driver ONLY when selection changes (prevents jitter)
    const prevSelectedRef = React.useRef(null);
    const prevFocusTypeRef = React.useRef(null);
    useEffect(() => {
        if (!mapRef.current) return;
        
        if (prevSelectedRef.current !== selectedDriver || prevFocusTypeRef.current !== focusType) {
            prevSelectedRef.current = selectedDriver;
            prevFocusTypeRef.current = focusType;
            
            if (selectedDriver) {
                if (focusType === 'car') {
                    const driverObj = driversOnMap.find(d => d._id === selectedDriver);
                    if (driverObj) {
                        const coords = driverObj.currentLocation?.coordinates || driverObj.location?.coordinates;
                        if (Array.isArray(coords) && coords.length === 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number' && coords[0] !== 0 && coords[1] !== 0) {
                            mapRef.current.panTo({ lat: coords[1], lng: coords[0] });
                            mapRef.current.setZoom(15);
                        }
                    }
                } else if (focusType === 'route') {
                    const assignedRoute = routes.find(r => String(r.driverId?._id || r.driverId) === String(selectedDriver));
                    const assignedJob = jobs.find(j => (j.status === 'assigned' || j.status === 'accepted' || j.status === 'started' || j.status === 'in_progress') && String(j.driverId?._id || j.driverId) === String(selectedDriver));
                    let boundsToFit = null;
                    if (assignedRoute && directionsResponses[assignedRoute._id]) {
                        boundsToFit = directionsResponses[assignedRoute._id].routes[0].bounds;
                    } else if (assignedJob && directionsResponses[`job-${assignedJob._id}`]) {
                        boundsToFit = directionsResponses[`job-${assignedJob._id}`].routes[0].bounds;
                    }
                    if (boundsToFit) {
                        mapRef.current.fitBounds(boundsToFit);
                    }
                }
            } else {
                // Deselected - zoom out to all drivers on map
                const bounds = new window.google.maps.LatLngBounds();
                let hasValid = false;
                driversOnMap.forEach(d => {
                    const coords = d.currentLocation?.coordinates || d.location?.coordinates;
                    if (Array.isArray(coords) && coords.length === 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number' && coords[0] !== 0 && coords[1] !== 0) {
                        bounds.extend({ lat: coords[1], lng: coords[0] });
                        hasValid = true;
                    }
                });

                routes.filter(r => r.isActive && r.schoolId && r.schoolId.location).forEach(r => {
                    const coords = r.schoolId.location.coordinates;
                    if (Array.isArray(coords) && coords.length === 2) {
                        bounds.extend({ lat: coords[1], lng: coords[0] });
                    }
                    if (r.startLocation?.coordinates?.length === 2) {
                        bounds.extend({ lat: r.startLocation.coordinates[1], lng: r.startLocation.coordinates[0] });
                    }
                    if (r.endLocation?.coordinates?.length === 2) {
                        bounds.extend({ lat: r.endLocation.coordinates[1], lng: r.endLocation.coordinates[0] });
                    }
                });

                jobs.filter(j => (j.status === 'assigned' || j.status === 'accepted' || j.status === 'started' || j.status === 'in_progress') && j.driverId).forEach(j => {
                    if (j.pickupCoordinates?.length === 2) {
                        bounds.extend({ lat: j.pickupCoordinates[1], lng: j.pickupCoordinates[0] });
                    }
                    if (j.dropoffCoordinates?.length === 2) {
                        bounds.extend({ lat: j.dropoffCoordinates[1], lng: j.dropoffCoordinates[0] });
                    }
                });

                if (hasValid) {
                    mapRef.current.fitBounds(bounds);
                }
            }
        }
    }, [selectedDriver, focusType, driversOnMap, routes, jobs, directionsResponses]);
    
    const directionsCallback = useCallback((response, routeId, status) => {
        if (response !== null && status === 'OK') {
            setDirectionsResponses(prev => prev[routeId] ? prev : { ...prev, [routeId]: response });
        }
    }, []);

    const statCards = [
        { label: 'Total Drivers', value: stats?.totalDrivers ?? '—', change: `${stats?.activeDrivers ?? 0} active`, trend: 'up', icon: icons.drivers, gradient: 'from-amber-500 to-orange-600', shadowColor: 'shadow-orange-500/20' },
        { label: 'Total PAs', value: stats?.totalPAs ?? '—', change: `${stats?.activePAs ?? 0} active`, trend: 'up', icon: icons.pa, gradient: 'from-violet-500 to-purple-600', shadowColor: 'shadow-purple-500/20' },
        { label: 'Active Routes', value: stats?.totalRoutes ?? '—', change: `${routes.length} configured`, trend: 'up', icon: icons.routes, gradient: 'from-indigo-500 to-blue-600', shadowColor: 'shadow-indigo-500/20' },
        { label: 'School Children', value: stats?.totalChildren ?? '—', change: `enrolled`, trend: 'up', icon: icons.children, gradient: 'from-emerald-500 to-teal-600', shadowColor: 'shadow-emerald-500/20' },
        { label: 'Fleet Vehicles', value: stats?.totalVehicles ?? '—', change: `in fleet`, trend: 'up', icon: icons.vehicle, gradient: 'from-rose-500 to-pink-600', shadowColor: 'shadow-rose-500/20' },
    ];

    const StatSkeleton = () => (
        <div className="bg-white/60 dark:bg-slate-900/40 backdrop-blur-3xl rounded-[2rem] p-6 border border-white/50 dark:border-white/5 animate-pulse shadow-sm">
            <div className="flex items-start justify-between mb-5">
                <div className="w-12 h-12 rounded-2xl bg-slate-200/50 dark:bg-slate-800" />
                <div className="w-20 h-5 rounded-full bg-slate-200/50 dark:bg-slate-800" />
            </div>
            <div className="w-16 h-8 bg-slate-200/50 dark:bg-slate-800 rounded mb-2" />
            <div className="w-24 h-4 bg-slate-200/30 dark:bg-slate-800/50 rounded" />
        </div>
    );

    // Provide custom actions for AdminLayout top bar
    const Actions = (
        <button onClick={() => fetchData(true)}
            className={`p-2 rounded-xl text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-amber-400 hover:bg-white/50 dark:hover:bg-slate-800/50 transition-all ${refreshing ? 'animate-spin' : ''}`}
            title="Refresh Data">
            {icons.refresh}
        </button>
    );

    return (
        <AdminLayout 
            title="Dashboard Overview" 
            subtitle="Real-time system overview and fleet status" 
            searchTerm={searchTerm} 
            onSearchChange={setSearchTerm}
            actions={Actions}
        >
            <div className="max-w-[1600px] mx-auto space-y-6">
                
                {/* Responsive Neon Glowing Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6">
                    {loading ? [...Array(5)].map((_, i) => <StatSkeleton key={i} />) : statCards.map((stat, i) => (
                        <div key={i} className="group relative overflow-hidden bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl rounded-[2rem] p-5 sm:p-6 border border-white/60 dark:border-white/5 shadow-sm hover:shadow-xl dark:shadow-[0_8px_30px_rgb(0,0,0,0.12)] transition-all duration-500">
                            {/* Ambient Glow */}
                            <div className={`absolute -inset-4 bg-gradient-to-br ${stat.gradient} opacity-[0.03] dark:opacity-[0.08] group-hover:opacity-[0.08] dark:group-hover:opacity-[0.15] blur-2xl transition-opacity duration-500`} />
                            
                            <div className="flex items-start justify-between mb-6 relative">
                                <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center text-white shadow-lg ${stat.shadowColor} group-hover:scale-110 group-hover:-rotate-3 transition-all duration-300`}>
                                    {stat.icon}
                                </div>
                                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold tracking-widest uppercase bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                                    {icons.arrowUp}
                                    {stat.change}
                                </div>
                            </div>
                            
                            <p className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight relative mb-1">{stat.value}</p>
                            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider relative">{stat.label}</p>
                        </div>
                    ))}
                </div>

                {/* Main Content Area - Dispatcher Style Map & Sidebar */}
                <div 
                    ref={mapContainerRef}
                    className={isFullscreen ? 'fixed inset-0 z-[100] bg-slate-50 dark:bg-slate-900 flex flex-col lg:flex-row p-4 sm:p-6 gap-4 sm:gap-6 overflow-hidden' : 'flex flex-col lg:flex-row gap-6 mt-6'}
                >
                        {/* Map Area */}
                        <div 
                            className={`relative group flex-1 bg-white/60 dark:bg-slate-900/40 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 overflow-hidden shadow-sm dark:shadow-2xl ${isFullscreen ? 'h-full' : 'min-h-[500px]'}`}
                        >
                            {/* Toggle Panel Button (Visible when list is hidden) */}
                            {!showDriverPanel && (
                                <div className="absolute top-6 right-20 z-20 flex gap-2">
                                    <button
                                        onClick={() => setShowDriverPanel(true)}
                                        className="w-10 h-10 rounded-xl bg-indigo-500/90 backdrop-blur-md text-white flex items-center justify-center hover:bg-indigo-600 transition-all shadow-lg active:scale-95 border border-white/20"
                                        title="Show Drivers List"
                                    >
                                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="8" y1="6" x2="21" y2="6" />
                                            <line x1="8" y1="12" x2="21" y2="12" />
                                            <line x1="8" y1="18" x2="21" y2="18" />
                                            <line x1="3" y1="6" x2="3.01" y2="6" />
                                            <line x1="3" y1="12" x2="3.01" y2="12" />
                                            <line x1="3" y1="18" x2="3.01" y2="18" />
                                        </svg>
                                    </button>
                                </div>
                            )}

                            {/* Fullscreen Toggle Button */}
                            <div className="absolute top-6 right-6 z-20 flex gap-2">
                                <button
                                    onClick={toggleFullscreen}
                                    className="w-10 h-10 rounded-xl bg-slate-900/80 dark:bg-slate-800/80 backdrop-blur-md text-white flex items-center justify-center hover:bg-slate-800 dark:hover:bg-slate-700 transition-all shadow-lg active:scale-95 border border-white/10"
                                    title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                                >
                                    {isFullscreen ? (
                                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="4 14 10 14 10 20" />
                                            <polyline points="20 10 14 10 14 4" />
                                            <line x1="14" y1="10" x2="21" y2="3" />
                                            <line x1="3" y1="21" x2="10" y2="14" />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="15 3 21 3 21 9" />
                                            <polyline points="9 21 3 21 3 15" />
                                            <line x1="21" y1="3" x2="14" y2="10" />
                                            <line x1="3" y1="21" x2="10" y2="14" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        {/* Socket Connection Status Badge */}
                        <div className="absolute top-3 left-3 z-20 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-sm text-[10px] font-semibold shadow-lg">
                            <span className={`w-2 h-2 rounded-full ${socketStatus === 'connected' ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
                            <span className={socketStatus === 'connected' ? 'text-emerald-300' : 'text-red-300'}>
                                {socketStatus === 'connected' ? 'LIVE' : 'OFFLINE'}
                            </span>
                            {eventCount > 0 && (
                                <span className="text-slate-400 ml-0.5">• {eventCount} events</span>
                            )}
                        </div>






                        {/* Google Map */}
                        <div className="absolute inset-0">
                            {isLoaded ? (
                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    center={defaultCenter}
                                    zoom={11}
                                    onLoad={onLoad}
                                    onUnmount={onUnmount}
                                    options={mapOptions}
                                >
                                    {driversOnMap.filter(d => (!searchTerm || (d.name && d.name.toLowerCase().includes(searchTerm.toLowerCase())) || (d.email && d.email.toLowerCase().includes(searchTerm.toLowerCase())) || (d.phone && d.phone.includes(searchTerm)))).map((driver) => {
                                        // User specifically requested: hide cars when no driver is selected, and show only the selected driver's car.
                                        if (!selectedDriver || selectedDriver !== driver._id) return null;

                                        const coords = driver.currentLocation?.coordinates || driver.location?.coordinates;
                                        const position = (Array.isArray(coords) && coords.length === 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number' && coords[0] !== 0 && coords[1] !== 0)
                                            ? { lat: coords[1], lng: coords[0] }
                                            : { lat: 51.5074, lng: -0.1278 };
                                        const driverSpeed = driver.speed || 0;
                                        const markerState = selectedDriver === driver._id ? 'selected' : driver.isActive ? 'active' : 'idle';
                                        const originalIndex = driversOnMap.findIndex(d => d._id === driver._id);
                                            
                                        return (
                                            <AnimatedMarker
                                                key={driver._id}
                                                map={mapRef.current}
                                                position={position}
                                                heading={driver.heading || 0}
                                                speed={driverSpeed}
                                                iconGenerator={(heading) => getCarMarkerIcon(markerState, heading, 0.6, originalIndex !== -1 ? originalIndex : 0)}
                                                onClick={() => {
                                                    setSelectedDriver(driver._id);
                                                    setShowInfoCard(true);
                                                }}
                                            />
                                        );
                                    })}

                                    {/* Driver Info Popup */}
                                    {selectedDriver && (() => {
                                        const driverObj = driversOnMap.find(d => d._id === selectedDriver);
                                        if (!driverObj) return null;
                                        const coords = driverObj.currentLocation?.coordinates || driverObj.location?.coordinates;
                                        const position = (coords && coords.length === 2 && coords[0] !== 0 && coords[1] !== 0)
                                            ? { lat: coords[1], lng: coords[0] }
                                            : { lat: 51.5074, lng: -0.1278 };
                                        const assignedRoute = routes.find(r => String(r.driverId?._id || r.driverId) === String(selectedDriver));
                                        const assignedJob = jobs.find(j => (j.status === 'assigned' || j.status === 'accepted' || j.status === 'started' || j.status === 'in_progress') && String(j.driverId?._id || j.driverId) === String(selectedDriver));
                                        return (
                                            <DriverInfoWindow
                                                driverObj={driverObj}
                                                position={position}
                                                assignedRoute={assignedRoute}
                                                assignedJob={assignedJob}
                                                onClose={() => setShowInfoCard(false)}
                                                showInfoCard={showInfoCard}
                                                setShowInfoCard={setShowInfoCard}
                                            />
                                        );
                                    })()}
                                    
                                    {routes.filter(r => r.isActive && r.driverId && r.schoolId && (!searchTerm || (r.name && r.name.toLowerCase().includes(searchTerm.toLowerCase())) || (r.driverId && r.driverId.name && r.driverId.name.toLowerCase().includes(searchTerm.toLowerCase())))).map(route => {
                                        const routeDriverId = String(route.driverId?._id || route.driverId);

                                        let schoolPos = null;
                                        if (route.schoolId.location?.coordinates?.length === 2) {
                                            schoolPos = { lat: route.schoolId.location.coordinates[1], lng: route.schoolId.location.coordinates[0] };
                                        }
                                        
                                        // Professionally calculate start position (fallback to first child, then driver location, then address)
                                        let startPos;
                                        if (route.startLocation?.coordinates?.length === 2) {
                                            startPos = { lat: route.startLocation.coordinates[1], lng: route.startLocation.coordinates[0] };
                                        } else if (route.children && route.children.length > 0 && route.children[0].pickupLocation?.coordinates?.length === 2) {
                                            startPos = { lat: route.children[0].pickupLocation.coordinates[1], lng: route.children[0].pickupLocation.coordinates[0] };
                                        } else {
                                            const driverObj = driversOnMap.find(d => String(d._id) === routeDriverId);
                                            if (driverObj && driverObj.currentLocation?.coordinates?.length === 2) {
                                                startPos = { lat: driverObj.currentLocation.coordinates[1], lng: driverObj.currentLocation.coordinates[0] };
                                            } else if (schoolPos) {
                                                startPos = { lat: schoolPos.lat - 0.02, lng: schoolPos.lng - 0.02 };
                                            } else {
                                                startPos = route.schoolId.address || route.schoolId.name || 'London, UK';
                                            }
                                        }

                                        let endPos;
                                        if (route.endLocation?.coordinates?.length === 2) {
                                            endPos = { lat: route.endLocation.coordinates[1], lng: route.endLocation.coordinates[0] };
                                        } else if (schoolPos) {
                                            endPos = schoolPos;
                                        } else {
                                            endPos = route.schoolId.address || route.schoolId.name || 'London, UK';
                                        }
                                        
                                        // Add waypoints for children to generate A, B, C markers on the map
                                        const waypoints = (route.children || [])
                                            .filter(c => c.pickupLocation?.coordinates?.length === 2)
                                            .map(c => ({
                                                location: { lat: c.pickupLocation.coordinates[1], lng: c.pickupLocation.coordinates[0] },
                                                stopover: true
                                            }));
                                        
                                        if (directionsResponses[route._id]) {
                                            const isThisDriverSelected = selectedDriver && selectedDriver === routeDriverId;
                                            // const isAnyDriverSelected = !!selectedDriver;
                                            const driverIndex = driversOnMap.findIndex(d => String(d._id) === routeDriverId);
                                            const routeColor = driverIndex >= 0 ? ['#10b981', '#0ea5e9', '#f59e0b', '#f43f5e', '#8b5cf6'][driverIndex % 5] : '#8b5cf6';
                                            
                                            if (selectedDriver && !isThisDriverSelected) return null;

                                            // Extract marker positions to draw custom professional markers
                                            const markersToDraw = [];
                                            directionsResponses[route._id].routes[0].legs.forEach((l, idx) => {
                                                markersToDraw.push({ pos: { lat: l.start_location.lat(), lng: l.start_location.lng() }, type: idx === 0 ? 'start' : 'waypoint' });
                                            });
                                            const lastLeg = directionsResponses[route._id].routes[0].legs[directionsResponses[route._id].routes[0].legs.length - 1];
                                            markersToDraw.push({ pos: { lat: lastLeg.end_location.lat(), lng: lastLeg.end_location.lng() }, type: 'end' });

                                            return (
                                                <React.Fragment key={`frag-${route._id}`}>
                                                    <DirectionsRenderer options={{ directions: directionsResponses[route._id], preserveViewport: true, suppressMarkers: true, polylineOptions: { strokeColor: routeColor, strokeWeight: isThisDriverSelected ? 6 : 4, strokeOpacity: 1.0, zIndex: isThisDriverSelected ? 50 : 1 } }} />
                                                    {markersToDraw.map((m, i) => (
                                                        <Marker key={`m-${i}`} position={m.pos} icon={{ path: window.google.maps.SymbolPath.CIRCLE, fillColor: m.type === 'start' ? '#10b981' : m.type === 'end' ? '#f43f5e' : '#f59e0b', fillOpacity: 1, strokeWeight: 2, strokeColor: 'white', scale: m.type === 'waypoint' ? 6 : 8 }} zIndex={isThisDriverSelected ? 51 : 2} />
                                                    ))}
                                                </React.Fragment>
                                            );
                                        } else {
                                            return <DirectionsService key={`svc-${route._id}`} options={{ origin: route.type === 'morning' ? startPos : endPos, destination: route.type === 'morning' ? endPos : startPos, waypoints: route.type === 'morning' ? waypoints : [...waypoints].reverse(), travelMode: 'DRIVING' }} callback={(response, status) => directionsCallback(response, route._id, status)} />;
                                        }
                                    })}

                                    {/* Render Active Private Job Route Polylines */}
                                    {jobs.filter(j => (j.status === 'assigned' || j.status === 'accepted' || j.status === 'started' || j.status === 'in_progress') && j.driverId).map(job => {
                                        const pickupPos = job.pickupCoordinates?.length === 2
                                            ? { lat: job.pickupCoordinates[1], lng: job.pickupCoordinates[0] }
                                            : null;
                                        const dropoffPos = job.dropoffCoordinates?.length === 2
                                            ? { lat: job.dropoffCoordinates[1], lng: job.dropoffCoordinates[0] }
                                            : null;
                                        const origin = pickupPos || job.pickupAddress;
                                        const dest = dropoffPos || job.dropoffAddress;
                                        if (!origin || !dest) return null;

                                        const jobDriverId = String(job.driverId?._id || job.driverId);

                                        if (directionsResponses[`job-${job._id}`]) {
                                            const driverIndex = driversOnMap.findIndex(d => String(d._id) === jobDriverId);
                                            const routeColor = driverIndex >= 0 ? ['#10b981', '#0ea5e9', '#f59e0b', '#f43f5e', '#8b5cf6'][driverIndex % 5] : '#8b5cf6';
                                            const isThisJobSelected = selectedDriver && selectedDriver === jobDriverId;
                                            if (selectedDriver && !isThisJobSelected) return null;
                                            const leg = directionsResponses[`job-${job._id}`].routes[0].legs[0];
                                            const markersToDraw = [
                                                { pos: { lat: leg.start_location.lat(), lng: leg.start_location.lng() }, type: 'start' },
                                                { pos: { lat: leg.end_location.lat(), lng: leg.end_location.lng() }, type: 'end' }
                                            ];

                                            return (
                                                <React.Fragment key={`frag-job-${job._id}`}>
                                                    <DirectionsRenderer options={{ directions: directionsResponses[`job-${job._id}`], preserveViewport: true, suppressMarkers: true, polylineOptions: { strokeColor: routeColor, strokeWeight: isThisJobSelected ? 6 : 4, strokeOpacity: 1.0, zIndex: isThisJobSelected ? 50 : 1 } }} />
                                                    {markersToDraw.map((m, i) => (
                                                        <Marker key={`m-job-${i}`} position={m.pos} icon={{ path: window.google.maps.SymbolPath.CIRCLE, fillColor: m.type === 'start' ? '#10b981' : '#f43f5e', fillOpacity: 1, strokeWeight: 2, strokeColor: 'white', scale: 8 }} zIndex={isThisJobSelected ? 51 : 2} />
                                                    ))}
                                                </React.Fragment>
                                            );
                                        } else {
                                            return <DirectionsService key={`svc-job-${job._id}`} options={{ origin, destination: dest, travelMode: 'DRIVING' }} callback={(response, status) => directionsCallback(response, `job-${job._id}`, status)} />;
                                        }
                                    })}
                                </GoogleMap>
                            ) : (
                                <div className="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Driver List Panel */}
                    {showDriverPanel && (
                        <div className={`w-full lg:w-80 bg-white/60 dark:bg-slate-900/40 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 overflow-hidden shadow-sm dark:shadow-2xl flex flex-col ${isFullscreen ? 'h-full' : ''}`} style={{ minHeight: isFullscreen ? 'auto' : '500px' }}>
                            <div className="p-6 border-b border-slate-200/50 dark:border-white/5 flex justify-between items-start">
                                <div>
                                    <h3 className="text-base font-black text-slate-900 dark:text-white tracking-tight">Drivers on Route</h3>
                                    <p className="text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 mt-1">Only drivers with assigned routes</p>
                                </div>
                                <button 
                                    onClick={() => setShowDriverPanel(false)} 
                                    className="p-2 -mr-2 -mt-2 text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                                    title="Hide List"
                                >
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-3 space-y-1">
                            {loading ? (
                                [...Array(6)].map((_, i) => (
                                    <div key={i} className="animate-pulse flex items-center gap-3 p-3">
                                        <div className="w-9 h-9 bg-slate-100 dark:bg-slate-800 rounded-xl" />
                                        <div className="flex-1 space-y-1.5"><div className="h-3 bg-slate-100 dark:bg-slate-800 rounded w-24" /><div className="h-2 bg-slate-50 dark:bg-slate-800/50 rounded w-16" /></div>
                                    </div>
                                ))
                            ) : activeDrivers.length === 0 ? (
                                <div className="py-12 text-center">
                                    <div className="w-12 h-12 mx-auto rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
                                        <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-slate-400">
                                            <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" />
                                        </svg>
                                    </div>
                                    <p className="text-sm text-slate-400 font-medium">No drivers yet</p>
                                </div>
                            ) : (
                                <>
                                    {/* ── Drivers with Assigned Routes ── */}
                                    {driversOnMap.map((d, i) => (
                                        <div key={d._id}
                                            className={`w-full flex items-center gap-4 p-3.5 rounded-2xl transition-all duration-300 text-left hover:scale-[1.02] ${selectedDriver === d._id ? 'bg-indigo-500/10 border border-indigo-500/30 shadow-sm' : 'hover:bg-slate-100/50 dark:hover:bg-slate-800/50'
                                                }`}>
                                            <div className="relative shrink-0">
                                                <div className={`w-11 h-11 rounded-[14px] bg-gradient-to-br ${['from-emerald-400 to-teal-500', 'from-sky-400 to-blue-500', 'from-amber-400 to-orange-500', 'from-rose-400 to-pink-500', 'from-violet-400 to-purple-500'][i % 5]} flex items-center justify-center text-[11px] font-black text-white shadow-lg`}>
                                                    {d.name?.split(' ').map(n => n[0]).join('')}
                                                </div>
                                                <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white dark:border-slate-800 shadow-sm bg-indigo-500" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{d.name}</p>
                                                <p className="text-[10px] font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400 mt-0.5">
                                                    {(() => {
                                                        const assignedRoute = routes.find(r => (r.driverId?._id || r.driverId) === d._id);
                                                        const assignedJob = jobs.find(j => (j.status === 'assigned' || j.status === 'accepted' || j.status === 'started' || j.status === 'in_progress') && String(j.driverId?._id || j.driverId) === String(d._id));
                                                        return assignedRoute ? assignedRoute.name : (assignedJob ? assignedJob.name : 'On Duty');
                                                    })()}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-1.5 shrink-0">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (selectedDriver === d._id && focusType === 'route') {
                                                            setSelectedDriver(null);
                                                            setFocusType(null);
                                                        } else {
                                                            setSelectedDriver(d._id);
                                                            setFocusType('route');
                                                        }
                                                    }}
                                                    title="Focus Map on Route Path"
                                                    className={`p-2 rounded-xl transition-all duration-300 ${selectedDriver === d._id && focusType === 'route' ? 'bg-indigo-500 text-white shadow-md shadow-indigo-500/20' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-500/10'}`}
                                                >
                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (selectedDriver === d._id && focusType === 'car') {
                                                            setSelectedDriver(null);
                                                            setFocusType(null);
                                                        } else {
                                                            setSelectedDriver(d._id);
                                                            setFocusType('car');
                                                        }
                                                    }}
                                                    title="Focus Map on Live Car Location"
                                                    className={`p-2 rounded-xl transition-all duration-300 ${selectedDriver === d._id && focusType === 'car' ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-500/10'}`}
                                                >
                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedDriver(d._id);
                                                        setShowInfoCard(true);
                                                    }}
                                                    title="Show Driver Info"
                                                    className={`p-2 rounded-xl transition-all duration-300 ${selectedDriver === d._id && showInfoCard ? 'bg-amber-500 text-white shadow-md shadow-amber-500/20' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-500/10'}`}
                                                >
                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    ))}

                                    {/* ── Unverified Drivers Section ── */}
                                    {drivers.filter(d => !d.isBlocked && !d.isVerified).length > 0 && (
                                        <>
                                            <div className="pt-4 pb-2 px-3">
                                                <div className="flex items-center gap-2">
                                                    <div className="h-px flex-1 bg-amber-300/30 dark:bg-amber-500/20" />
                                                    <span className="text-[9px] font-black tracking-widest uppercase text-amber-500 dark:text-amber-400">Unverified</span>
                                                    <div className="h-px flex-1 bg-amber-300/30 dark:bg-amber-500/20" />
                                                </div>
                                            </div>
                                            {drivers.filter(d => !d.isBlocked && !d.isVerified).map((d, i) => (
                                                <div key={d._id}
                                                    className="w-full flex items-center gap-4 p-3.5 rounded-2xl opacity-70 bg-amber-50/30 dark:bg-amber-500/5 border border-amber-200/30 dark:border-amber-500/10">
                                                    <div className="relative">
                                                        <div className="w-11 h-11 rounded-[14px] bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-[11px] font-black text-white shadow-lg">
                                                            {d.name?.split(' ').map(n => n[0]).join('')}
                                                        </div>
                                                        <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white dark:border-slate-800 shadow-sm bg-amber-500" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-bold text-slate-700 dark:text-slate-300 truncate">{d.name}</p>
                                                        <p className="text-[10px] font-black tracking-widest uppercase text-amber-600 dark:text-amber-400 mt-0.5">
                                                            <span className="flex items-center gap-1"><svg className="w-3.5 h-3.5 flex-shrink-0 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg> Docs Pending</span>
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    </AdminLayout>
    );
};

export default AdminDashboard;
